import type { ReactNode } from "react";
import { Button } from "@fluentui/react-components";
import type { Finding, FindingStatus } from "../types";
import { isLocatable } from "../location";
import { CheckIcon } from "../../shared/components/icons";
import styles from "./FindingCard.module.css";

interface FindingCardProps {
  readonly finding: Finding;
  /** True while this finding's apply/skip request is in flight. */
  readonly busy: boolean;
  readonly onApply: () => void;
  readonly onSkip: () => void;
  readonly onJumpToLocation: () => void;
}

/** One rule id (AIVA-8087's descriptive snake_case names) → a short display
 *  label, grouped by which of the two checks it belongs to. */
const RULE_LABELS: Record<string, string> = {
  missing_hyperlink: "Missing hyperlink · Cross-reference",
  broken_reference: "Broken reference · Cross-reference",
  bracket_heading_mismatch: "Bracket heading mismatch · Cross-reference",
  topic_mismatch: "Topic mismatch · Cross-reference",
  inconsistent_formatting: "Inconsistent formatting · Cross-reference",
  exhibit_schedule_mismatch: "Exhibit/Schedule mismatch · Cross-reference",
  defined_term_section_mismatch: "Defined term section mismatch · Cross-reference",
  hyperlink_target_mismatch: "Hyperlink target mismatch · Cross-reference",
  shortened_hyperlink_text: "Shortened hyperlink text · Cross-reference",
  numbering_order: "Numbering order · Cross-reference",
  ambiguous_reference: "Ambiguous reference · Cross-reference",
  defined_not_used: "Defined but not used · Defined term",
  used_not_defined: "Used but not defined · Defined term",
  alphabetical_order: "Alphabetical order · Defined term",
  missing_defined_term: "Missing defined term · Defined term",
};

/** Falls back to a Title Case rendering of an unrecognised snake_case rule
 *  id, rather than the raw id verbatim — keeps a future new rule readable
 *  in the UI even before this table is updated for it. */
function ruleLabel(ruleId: string): string {
  const known = RULE_LABELS[ruleId];
  if (known) return known;
  const titleCased = ruleId
    .split("_")
    .map((word) => (word ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join(" ");
  return `${titleCased} · Cross-reference`;
}

/** A short title derived from the finding's own problem text — the backend
 *  already writes a complete sentence, so the card just takes the first
 *  clause rather than duplicating title-authoring logic here.
 *
 *  The split point requires a letter (not a digit) right before the
 *  punctuation, so a quoted heading/section number like `"7. Confidentiality"
 *  is numbered out of sequence...` doesn't get cut down to just `"7.` — a
 *  plain `[.!?]` lookbehind treats every "N." inside the quoted subject as
 *  its own sentence end (alphabetical_order's/numbering_order's problem text always quotes one). */
function findingTitle(finding: Finding): string {
  const first = finding.problem.split(/(?<=[A-Za-z][.!?])\s/)[0];
  return first || finding.comment || "Review this finding";
}

const DECIDED_STATUSES = new Set(["applied", "skipped", "verified"]);

function statusClassName(status: FindingStatus, stale: boolean): string {
  if (status === "applied" || status === "verified") return styles.applied ?? "";
  if (status === "skipped") return styles.skipped ?? "";
  if (stale) return styles.stale ?? "";
  return "";
}

/**
 * One cross-reference/defined-terms finding (AIVA-7506) — a durable,
 * independently-actionable card. Unlike the in-chat approval cards, this one
 * is not tied to a specific turn or message: it is fetched from the findings
 * store and stays actionable across the whole review session.
 */
export function FindingCard({ finding, busy, onApply, onSkip, onJumpToLocation }: FindingCardProps) {
  const decided = DECIDED_STATUSES.has(finding.status);
  const stale = finding.status === "stale";
  // Apply is blocked on a stale finding — writing against it would just be
  // refused again by the fix's own staleness guard. Skip stays available so
  // the operator can dismiss a moot row without an it'll-just-fail click.
  //
  // AIVA-8209: also blocked on a finding with no location at all — neither
  // an anchor nor a usable matchText. Apply used to stay enabled there, and
  // the click was a trap: the fix refused for want of a location, the
  // finding was recorded `stale`, and Apply then disabled itself for good —
  // so one click silently destroyed a finding that was never broken, only
  // unanchored. A finding that still has its text is NOT blocked here: the
  // apply path re-anchors from that text and retries.
  const locatable = isLocatable(finding);
  const applyDisabled = decided || stale || busy || !locatable;
  const skipDisabled = decided || busy;
  // AIVA-8209: a finding with no anchor_tag used to make this whole area a
  // disabled button — so for every finding the anchor step never reached (or
  // whose anchor a later re-scan/cleanup removed) the click did nothing at
  // all, with not even a tooltip to explain it, since browsers suppress
  // titles on disabled elements. That is the "cards are no longer clickable"
  // report. Navigation now falls back to the finding's own text, so only a
  // finding with neither an anchor nor a usable matchText is truly inert.
  // Unlike apply/skip this still never depends on `busy`: jumping writes
  // nothing and is safe to run at any time.
  const jumpDisabled = !locatable;
  const statusClass = statusClassName(finding.status, stale);

  let stamp: ReactNode = null;
  if (finding.status === "applied" || finding.status === "verified") {
    stamp = (
      <span className={`${styles.stamp} ${styles.applied}`}>
        Applied <CheckIcon />
      </span>
    );
  } else if (finding.status === "skipped") {
    stamp = <span className={`${styles.stamp} ${styles.skipped}`}>Skipped</span>;
  }

  return (
    <div className={`${styles.card} ${statusClass}`}>
      {/*
       * Click-to-jump (AIVA-7506 follow-up): a dedicated "Jump to location"
       * button was one more thing to notice and click in an already busy
       * actions row. The descriptive area — everything except Apply/Skip/the
       * status stamp — is the jump target instead, the same way clicking a
       * comment bubble's body (not its buttons) is what Word's own Comments
       * pane treats as "go to this." A real <button>, not a div with
       * role="button", so keyboard activation (Enter/Space) and the disabled
       * state come from the browser instead of hand-rolled key handling.
       */}
      <button
        type="button"
        className={`${styles.top} ${jumpDisabled ? "" : styles.jumpable}`}
        disabled={jumpDisabled}
        title={jumpDisabled ? "No location recorded for this finding" : "Jump to this finding in the document"}
        onClick={jumpDisabled ? undefined : onJumpToLocation}
      >
        <div className={styles.icon} aria-hidden="true">
          !
        </div>
        <div className={styles.body}>
          <div className={styles.label}>{ruleLabel(finding.rule_id)}</div>
          <div className={styles.title}>{findingTitle(finding)}</div>
          {finding.comment && finding.comment !== finding.problem && (
            <div className={styles.description}>{finding.comment}</div>
          )}
        </div>
      </button>

      {stale && (
        <div className={styles.chips}>
          <span className={`${styles.chip} ${styles.warn}`}>Document changed — could not apply</span>
        </div>
      )}

      {finding.confidence !== null && (
        <div className={styles.confidence}>
          <div className={styles.confidenceBar}>
            <div className={styles.confidenceFill} style={{ width: `${Math.round(finding.confidence * 100)}%` }} />
          </div>
          {Math.round(finding.confidence * 100)}% confidence
        </div>
      )}

      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="small"
          disabled={applyDisabled}
          onClick={onApply}
          title={locatable ? undefined : "This finding no longer has a location in the document — re-run the check"}
        >
          Apply
        </Button>
        <Button appearance="secondary" size="small" disabled={skipDisabled} onClick={onSkip}>
          Skip
        </Button>
        {stamp}
      </div>
    </div>
  );
}
